import { z } from "zod";
import { defaultStringifySearch } from "@tanstack/react-router";
import { workFilterSchema } from "./work-queue";

/** Shared by the register and its bounded, URL-backed return context. */
export const frameworkSearchSchema = z.object({
  q: z.string().max(256).optional().catch(undefined),
  status: z.enum(["all", "findings", "compliant", "partial", "non_compliant", "not_applicable", "not_assessed"]).optional().catch(undefined),
  severity: z.enum(["all", "critical", "high", "medium", "low"]).optional().catch(undefined),
  family: z.string().max(128).optional().catch(undefined),
  know: z.enum(["all", "auto", "structural", "manual", "unchecked"]).optional().catch(undefined),
  met: z.enum(["all", "not_met", "partly"]).optional().catch(undefined),
  sort: z.enum(["attention", "instrument"]).optional().catch(undefined),
  reason: z.enum(["supporting_failed", "supporting_unavailable", "exception_lapsed", "not_met", "partly_met", "expiring", "unchecked", "unowned"]).optional().catch(undefined),
});
export type FrameworkSearch = z.infer<typeof frameworkSearchSchema>;

const frameworkRegisterSchema = z.object({
  frameworkId: z.string().min(1).max(128),
  frameworkVersion: z.string().min(1).max(64),
  filters: frameworkSearchSchema,
});
export type FrameworkRegister = z.infer<typeof frameworkRegisterSchema>;

export const complianceDetailSearchSchema = z.object({
  register: frameworkRegisterSchema.optional().catch(undefined),
  work: workFilterSchema.optional().catch(undefined),
});

/** Empty register state needs no return context in the detail URL. */
export function frameworkRegisterContext(
  frameworkId: string,
  frameworkVersion: string,
  search: FrameworkSearch,
): FrameworkRegister | undefined {
  const filters = frameworkSearchSchema.parse(search);
  if (!Object.values(filters).some((value) => value !== undefined)) return undefined;
  return { frameworkId, frameworkVersion, filters };
}

/** A control's own framework is authoritative, including after crosswalk navigation. */
export function frameworkReturnSearch(
  register: FrameworkRegister | undefined,
  frameworkId: string,
  frameworkVersion: string,
): FrameworkSearch {
  return register?.frameworkId === frameworkId && register.frameworkVersion === frameworkVersion
    ? frameworkSearchSchema.parse(register.filters)
    : {};
}

/** Breadcrumbs accept an href; build only a local framework path and known filters. */
export function frameworkRegisterHref(
  tenantSlug: string,
  frameworkId: string,
  frameworkVersion: string,
  search: FrameworkSearch,
): string {
  const path = `/tenants/${encodeURIComponent(tenantSlug)}/posture/compliance/frameworks/${encodeURIComponent(frameworkId)}/${encodeURIComponent(frameworkVersion)}`;
  // Match the router's JSON-aware parser: raw q=123 would become a number
  // and disappear during string-field validation.
  return `${path}${defaultStringifySearch(frameworkSearchSchema.parse(search))}`;
}
